import { XClose } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { BRIEF_MODAL_DIMENSIONS } from "@/utils/layout-constants";

interface ProductTheaterViewProps {
  isOpen: boolean;
  onClose: () => void;
  canCancel?: boolean;
}

export const ProductTheaterView = ({ isOpen, onClose, canCancel = true }: ProductTheaterViewProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 overflow-hidden">
      {/* Same modal size as product selection - responsive with max dimensions */}
      <div className={`relative bg-white rounded-lg ${BRIEF_MODAL_DIMENSIONS.SIZE} p-6 overflow-hidden`}>
        {/* Gradient background */}
        <TheaterGradientBackground />

        {/* Cancel button - top right, same position as product selection modal */}
        {canCancel && (
          <Button
            color="secondary"
            size="sm"
            onClick={onClose}
            className="absolute right-20 top-[60px] z-20 gap-1"
            iconLeading={<XClose className="h-5 w-5 text-quaternary" />}
          >
            Cancel
          </Button>
        )}

        {/* Video with mix-blend-multiply - centered */}
        <video
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[466px] h-[829px] object-cover mix-blend-multiply"
          src="/videos/smart-briefs-analysis.mov"
          autoPlay
          loop
          muted
          playsInline
        />

        {/* Loading text - positioned per Figma (705/893 ≈ 79% from top) */}
        <p className="absolute left-1/2 -translate-x-1/2 top-[85%] text-lg font-normal text-secondary leading-7">
          Analysing best performing videos...
        </p>
      </div>
    </div>
  );
};

/** Gradient background for theater view - matches Figma design */
const TheaterGradientBackground = () => (
  <svg
    className="absolute inset-0 w-full h-full"
    viewBox="0 0 1372 893"
    fill="none"
    preserveAspectRatio="xMidYMid slice"
  >
    <g clipPath="url(#clip0_theater)">
      <g filter="url(#filter0_f_theater)">
        <g opacity="0.9" filter="url(#filter1_f_theater)">
          <ellipse
            cx="400.331"
            cy="520.258"
            rx="202.135"
            ry="191.456"
            transform="rotate(-75.9057 400.331 520.258)"
            fill="#ADE6FF"
            fillOpacity="0.6"
          />
        </g>
        <g opacity="0.9" filter="url(#filter2_f_theater)">
          <ellipse
            cx="839.692"
            cy="508.785"
            rx="210.827"
            ry="185.028"
            transform="rotate(-75.9057 839.692 508.785)"
            fill="#FFC27B"
            fillOpacity="0.6"
          />
        </g>
        <g opacity="0.9" filter="url(#filter3_f_theater)">
          <ellipse
            cx="545.28"
            cy="485.176"
            rx="172.479"
            ry="166.344"
            transform="rotate(-75.9057 545.28 485.176)"
            fill="#FF9CDE"
            fillOpacity="0.6"
          />
        </g>
      </g>
    </g>
    <defs>
      <filter
        id="filter0_f_theater"
        x="-191.823"
        y="-100.645"
        width="1618.22"
        height="1222.47"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="200" result="effect1_foregroundBlur_theater" />
      </filter>
      <filter
        id="filter1_f_theater"
        x="168.177"
        y="278.694"
        width="464.307"
        height="483.127"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="20" result="effect1_foregroundBlur_theater" />
      </filter>
      <filter
        id="filter2_f_theater"
        x="612.986"
        y="259.355"
        width="453.412"
        height="498.86"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="20" result="effect1_foregroundBlur_theater" />
      </filter>
      <filter
        id="filter3_f_theater"
        x="338.525"
        y="273.015"
        width="413.509"
        height="424.322"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="20" result="effect1_foregroundBlur_theater" />
      </filter>
      <clipPath id="clip0_theater">
        <rect width="1440" height="960" fill="white" transform="translate(-34 -33.5)" />
      </clipPath>
    </defs>
  </svg>
);
